import { InformationalBanner } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Button, Checkbox, Form, Icon, Input, Label, Message, Popup } from 'semantic-ui-react';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DefaultDateFormat, formatDateInTimezone } from '../../../helpers/dates';
import { addError } from '../../../helpers/errors';
import {
  billingIntervalsEqual,
  getBillingServiceInterval,
  getDisplayInterval,
  getIntervalChangeBlockReason,
  getIntervalTransitionServicePeriod,
  getUpdatedBillingDates,
  isCadenceTransitionConfirmation,
  normalizeBillingInterval,
} from '../../../helpers/invoicePlans';
import { useInvoiceEditDetails, useInvoiceEditStudents } from '../../../hooks/useInvoiceEdit';
import { resolvePlanLocation } from '../../../hooks/useLocations';

import InlineError from '../../Messages/InlineError';
import { DatePicker } from '../../Shared/DatePicker';
import { Dropdown } from '../../Shared/Dropdown';
import SectionCard from '../../Shared/SectionCard';

import { SERVICE_DIRECTION } from '../../../contacts/enums';
import './Billing.scss';
import { StepsContainer } from './Steps';
import { BILLING_INTERVAL_OPTIONS } from './dropdownOptions';

export default function StepInvoicePlanDetails({ onSave, isEdit }) {
  const { t } = useTranslation();
  const { isEditBillingIntervalEnabled } = useFlags();
  const details = useInvoiceEditDetails();
  const selectedStudents = useInvoiceEditStudents();
  const planLocation = useSelector((state) =>
    resolvePlanLocation(
      state.locations?.list,
      details?.location,
      selectedStudents[0],
      state.students?.list,
      state.rooms?.list
    )
  );
  const hasAuthoritativeTimezone = !!planLocation?.timezone && !!moment.tz.zone(planLocation.timezone);
  const timezone = hasAuthoritativeTimezone ? planLocation.timezone : 'utc';

  const getInitialDateEnd = (initialDateEnd) => {
    if (typeof initialDateEnd === 'string') return initialDateEnd;
    return formatDateInTimezone(initialDateEnd, timezone);
  };

  const [billingInterval, setBillingInterval] = useState(normalizeBillingInterval(details?.billingInterval));
  const [originalBillingInterval] = useState(
    normalizeBillingInterval(details?.oldBillingInterval ?? details?.billingInterval)
  );
  const [dateStart, setDateStart] = useState(details?.dateStart);
  const [dateDue] = useState(details?.dateDue);
  const [displayInterval, setDisplayInterval] = useState('');
  const [displayPreviousServiceInterval, setDisplayPreviousServiceInterval] = useState('');
  const [displayNextServiceInterval, setDisplayNextServiceInterval] = useState('');
  const [dateEnd, setDateEnd] = useState(getInitialDateEnd(details?.dateEnd));
  const [isDateEndChanged, setIsDateEndChanged] = useState(false);
  const [isServiceDatesEnabled, setIsServiceDatesEnabled] = useState(
    details?.isServiceDatesEnabled !== undefined ? details?.isServiceDatesEnabled : true
  );
  const [serviceDirection, setServiceDirection] = useState(details?.serviceDirection ?? SERVICE_DIRECTION.Next);
  const [errors, setErrors] = useState();
  const canEditBillingInterval =
    !isEdit || isEditBillingIntervalEnabled || Boolean(details?.intervalTransitionRecovery);
  const isBillingIntervalChanged =
    canEditBillingInterval &&
    isEdit &&
    originalBillingInterval &&
    billingInterval &&
    !billingIntervalsEqual(originalBillingInterval, billingInterval);
  const isCadenceTransition = isCadenceTransitionConfirmation({
    ...details,
    oldBillingInterval: originalBillingInterval,
    billingInterval,
  });
  const locationUnavailable = isCadenceTransition && !hasAuthoritativeTimezone;
  const persistedEligibilityState = details?.intervalChangeExpectedState;
  const eligibilityPlan = persistedEligibilityState
    ? { ...details, ...persistedEligibilityState, isServiceDatesEnabled, serviceDirection }
    : { ...details, isServiceDatesEnabled, serviceDirection };
  const intervalChangeBlockReason =
    isBillingIntervalChanged && !locationUnavailable
      ? getIntervalChangeBlockReason({
          invoicePlan: eligibilityPlan,
          nextBillingInterval: billingInterval,
          timezone,
        })
      : null;

  const calculateServiceDate = useCallback(() => {
    if (locationUnavailable) {
      return { calculatedPreviousServiceInterval: '', calculatedNextServiceInterval: '' };
    }
    const recovery = details?.intervalTransitionRecovery;
    const transitionBoundary =
      recovery?.schedulerSnapshot?.dateServiceEnd ??
      persistedEligibilityState?.dateServiceEnd ??
      details?.dateServiceEnd;
    const retainedDue = recovery?.effectiveDue ?? persistedEligibilityState?.dateDue ?? dateDue;
    const transitionInterval = recovery?.nextBillingInterval ?? billingInterval;
    if (isCadenceTransition && isServiceDatesEnabled && transitionBoundary && retainedDue && details?.dateStart) {
      const transitionPeriod = getIntervalTransitionServicePeriod({
        dateServiceEnd: transitionBoundary,
        dateDue: retainedDue,
        dateStart: details.dateStart,
        billingInterval: transitionInterval,
        timezone,
      });
      return {
        calculatedPreviousServiceInterval: getBillingServiceInterval(
          dateDue || dateStart,
          transitionInterval,
          SERVICE_DIRECTION.Previous,
          timezone
        ),
        calculatedNextServiceInterval: transitionPeriod.displayInterval,
      };
    }
    return {
      calculatedPreviousServiceInterval: getBillingServiceInterval(
        dateDue || dateStart,
        billingInterval,
        SERVICE_DIRECTION.Previous,
        timezone
      ),
      calculatedNextServiceInterval: getBillingServiceInterval(
        dateDue || dateStart,
        billingInterval,
        SERVICE_DIRECTION.Next,
        timezone
      ),
    };
  }, [
    billingInterval,
    dateDue,
    dateStart,
    details?.dateServiceEnd,
    details?.dateStart,
    details?.intervalTransitionRecovery,
    isCadenceTransition,
    locationUnavailable,
    isServiceDatesEnabled,
    persistedEligibilityState?.dateDue,
    persistedEligibilityState?.dateServiceEnd,
    timezone,
  ]);

  useEffect(() => {
    if (dateStart && billingInterval && serviceDirection) {
      const { calculatedPreviousServiceInterval, calculatedNextServiceInterval } = calculateServiceDate();
      setDisplayPreviousServiceInterval(calculatedPreviousServiceInterval);
      setDisplayNextServiceInterval(calculatedNextServiceInterval);
    }
  }, [dateStart, billingInterval, serviceDirection, calculateServiceDate]);

  useEffect(() => {
    setDisplayInterval(getDisplayInterval(billingInterval));
  }, [billingInterval]);

  return (
    <>
      <StepsContainer isInvoice={false} CurrentForm={DetailsCard} onNext={onSaveLocal} />
    </>
  );
  function DetailsCard() {
    return (
      <SectionCard data-testid="invoice-plan-details" header={t('billing.invoicePlan.details.title')}>
        <DetailsForm />
      </SectionCard>
    );
  }
  function RenderNotificationDays() {
    const [notifyDays, setNotifyDays] = useState(details?.notificationDays ?? 1);
    // apply default
    details.notificationDays = notifyDays;

    function onBlurNotificationDays() {
      details.notificationDays = Number(notifyDays ?? 0);
      setErrors(null);
    }

    function onChangeNotifyDays(e, { value }) {
      setNotifyDays(value);
    }

    return (
      <Form.Group>
        <Form.Field inline>
          <label data-testid="notification-days-label">{t('billing.invoicePlan.details.notificationDaysLabel')}</label>
          <Input
            data-testid="notification-days-input"
            maxLength={2}
            className="notifications-days"
            name={'notificationDays'}
            value={notifyDays}
            placeholder="0"
            onChange={onChangeNotifyDays}
            onBlur={onBlurNotificationDays}
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          <label className="label-after" data-testid="notification-days-label-after">
            {t('billing.invoicePlan.details.notificationDaysSuffix')}
            <Popup
              trigger={<Icon size="small" color="grey" className={'plan-info'} name="question" circular />}
              content={t('billing.invoicePlan.details.notificationDaysHelp')}
            />
          </label>
        </Form.Field>
      </Form.Group>
    );
  }

  function DetailsForm() {
    const displayDateStartWithDay = formatDateInTimezone(dateStart, timezone, 'dddd, MM/DD/YYYY');
    const displayDateDueWithDay = formatDateInTimezone(dateDue, timezone, 'dddd, MM/DD/YYYY');
    const displayDateStart = formatDateInTimezone(dateStart, timezone);
    return (
      <>
        <Form.Group>
          <Form.Field width={4} error={!!errors?.billingInterval}>
            <Label data-testid="billing-interval-label" basic className="label-required" style={{ border: 'none' }}>
              {t('billing.invoicePlan.details.billingIntervalLabel')}
              <Popup
                wide
                trigger={<Icon size="small" color="grey" className={'plan-info'} name="question" circular />}
                content={
                  <div className="billing-interval-help" data-testid="billing-interval-help">
                    <p>{t('billing.intervalHelp.biweekly')}</p>
                    <p>{t('billing.intervalHelp.twicePerMonth')}</p>
                    <p>{t('billing.intervalHelp.note')}</p>
                  </div>
                }
              />
            </Label>
            <Dropdown
              data-testid="billing-interval-dropdown"
              clearable={!isEdit}
              disabled={!canEditBillingInterval}
              required
              onChange={onChangeBillingInterval}
              placeholder={t('billing.invoicePlan.details.billingIntervalPlaceholder')}
              name={'billingInterval'}
              value={billingInterval}
              options={BILLING_INTERVAL_OPTIONS}
              translator={t}
            />
            {errors?.billingInterval && <InlineError text={errors.billingInterval} />}
          </Form.Field>
          <Form.Field width={4}>
            <Label data-testid="date-start-label" basic className="label-required" style={{ border: 'none' }}>
              {t('invoicePlan.startDate')}
              <Popup
                trigger={<Icon size="small" color="grey" className={'plan-info'} name="question" circular />}
                content={t('invoicePlan.startDate.help')}
              />
            </Label>
            <span data-testid="date-start-datepicker">
              <DatePicker
                required
                disabled={isEdit}
                minDate={moment().tz(timezone)}
                name={'dateStart'}
                value={displayDateStart}
                onChange={onChangeDateStart}
              />
            </span>

            {errors?.dateStart && <InlineError text={errors.dateStart} />}
          </Form.Field>
          <Form.Field width={4}>
            <label data-testid="date-end-label" className={'plan-wrapper'}>
              {t('invoicePlan.endDate')}
              <Popup
                trigger={<Icon size="small" color="grey" className={'plan-info'} name="question" circular />}
                content={t('billing.invoicePlan.details.endDateHelp')}
              />
            </label>
            <span data-testid="date-end-datepicker">
              <DatePicker
                name={'dateEnd'}
                value={dateEnd}
                onChange={onChangeDateEnd}
                minDate={displayDateStart}
                disabled={displayDateStart ? false : true}
              />
            </span>
          </Form.Field>
        </Form.Group>

        {isBillingIntervalChanged && (
          <InformationalBanner
            type="warning"
            extraClasses="mb-4"
            data-testid="billing-interval-change-message"
            label={
              <div className="space-y-2" role="status">
                <strong>{t('billing.intervalChange.title')}</strong>
                <p data-testid="billing-interval-change-values">
                  <strong>{t(originalBillingInterval)}</strong> <span aria-hidden="true">→</span>{' '}
                  <strong>{t(billingInterval)}</strong>
                </p>
                {dateEnd && (
                  <p data-testid="billing-interval-end-date-unchanged">
                    {t('billing.intervalChange.endDateUnchanged', { dateEnd })}
                  </p>
                )}
                <p>{t('billing.intervalChange.description')}</p>
              </div>
            }
          />
        )}

        {intervalChangeBlockReason && (
          <div role="alert">
            <InformationalBanner
              type="error"
              extraClasses="mb-4"
              data-testid="billing-interval-boundary-error"
              label={getIntervalChangeBlockMessage(intervalChangeBlockReason, t)}
            />
          </div>
        )}
        {locationUnavailable && (
          <div role="alert">
            <InformationalBanner
              type="error"
              extraClasses="mb-4"
              data-testid="billing-interval-location-error"
              label={t('billing.intervalChange.locationUnavailable')}
            />
          </div>
        )}

        <RenderNotificationDays />

        {dateStart && billingInterval && (
          <Form.Group>
            <Form.Field inline className="flex">
              <Checkbox
                data-testid="service-dates-toggle"
                toggle
                label={t('invoiceplan.servicedate.toggleTitle')}
                checked={isServiceDatesEnabled}
                onChange={onChangeServiceDatesEnabled}
              />
            </Form.Field>
          </Form.Group>
        )}
        {dateStart && billingInterval && isServiceDatesEnabled && serviceDirection && (
          <>
            <Form.Group>
              <Form.Field inline>
                <label className="label-required" data-testid="service-date-label">
                  {t('invoiceplan.servicedate.title', {
                    displayInterval: t(displayInterval),
                  })}
                </label>
                <div className="billing-direction-group margin top bottom">
                  <Button.Group id="serviceDirection">
                    <Button
                      data-testid="service-date-previous-button"
                      className={serviceDirection === SERVICE_DIRECTION.Previous ? 'primary' : 'basic'}
                      onClick={() => onChangeServiceDirection(SERVICE_DIRECTION.Previous)}
                    >
                      {t('common.previous')} {t(displayInterval)}
                      <p>{displayPreviousServiceInterval}</p>
                    </Button>
                    <Button
                      data-testid="service-date-next-button"
                      className={serviceDirection === SERVICE_DIRECTION.Next ? 'primary' : 'basic'}
                      onClick={() => onChangeServiceDirection(SERVICE_DIRECTION.Next)}
                    >
                      {t('billing.invoicePlan.details.nextServicePeriodAction')} {t(displayInterval)}{' '}
                      <p>{displayNextServiceInterval}</p>
                    </Button>
                  </Button.Group>
                </div>
              </Form.Field>
            </Form.Group>
            <Message info data-testid="service-date-info-message">
              <Icon name="info circle" size="big" data-testid="service-date-info" />
              {!dateDue &&
                t('invoiceplan.invoice.info', {
                  dateStart: displayDateStartWithDay,
                  billingInterval: t(billingInterval?.toLowerCase()),
                  serviceDate:
                    serviceDirection === SERVICE_DIRECTION.Next
                      ? displayNextServiceInterval
                      : displayPreviousServiceInterval,
                })}
              {dateDue &&
                t('invoiceplan.invoice.info.next', {
                  dateDue: displayDateDueWithDay,
                  billingInterval: t(billingInterval?.toLowerCase()),
                  serviceDate:
                    serviceDirection === SERVICE_DIRECTION.Next
                      ? displayNextServiceInterval
                      : displayPreviousServiceInterval,
                })}
            </Message>
          </>
        )}

        {!isServiceDatesEnabled && dateStart && (
          <Message info data-testid="service-date-shortInfo-message">
            <Icon name="info circle" size="big" />
            {!dateDue &&
              t('invoiceplan.invoice.shortInfo', {
                dateStart: displayDateStartWithDay,
                billingInterval: t(billingInterval?.toLowerCase()),
              })}
            {dateDue &&
              t('invoiceplan.invoice.shortInfo.next', {
                dateDue: displayDateDueWithDay,
                billingInterval: t(billingInterval?.toLowerCase()),
              })}
          </Message>
        )}
      </>
    );
  }

  function validate() {
    let errors = null;
    if (!billingInterval)
      errors = addError(errors, 'billingInterval', t('billing.stepInvoicePlanDetail.BillingIntervalisrequired'));
    if (!dateStart) errors = addError(errors, 'dateStart', t('enrollments.enrollmentInfoStartDateRequired'));
    if (intervalChangeBlockReason) {
      errors = addError(errors, 'billingInterval', getIntervalChangeBlockMessage(intervalChangeBlockReason, t));
    }
    if (locationUnavailable) {
      errors = addError(errors, 'billingInterval', t('billing.intervalChange.locationUnavailable'));
    }

    return errors;
  }
  function onSaveLocal(doValidate = true) {
    const errors = doValidate ? validate() : false;

    if (errors) {
      setErrors(errors);
      return false;
    } else if (onSave) {
      if (details?.intervalTransitionRecovery) {
        onSave({
          ...details,
          dateEnd: isEdit && !isDateEndChanged ? details.dateEnd : dateEnd,
          billingInterval: details.intervalTransitionRecovery.nextBillingInterval,
          dateDue: details.intervalTransitionRecovery.effectiveDue,
          dateSend: details.intervalTransitionRecovery.schedulerSnapshot.dateSend,
          notificationDays: details.notificationDays ?? 0,
          location: planLocation?.id,
          ...(displayNextServiceInterval ? { intervalChangeServicePeriodPreview: displayNextServiceInterval } : {}),
        });
        return true;
      }
      // Calculate updated billing dates, including dateSend based on notificationDays
      const invoicePlanData = {
        dateStart,
        dateEnd: isEdit && !isDateEndChanged ? details?.dateEnd : dateEnd,
        dateDue: dateDue || dateStart,
        notificationDays: details.notificationDays ?? 1,
        billingInterval,
      };

      let updatedDates;
      try {
        updatedDates = getUpdatedBillingDates({
          invoicePlan: invoicePlanData,
          timezone: timezone,
          isEditing: true,
        });
      } catch (error) {
        setErrors(addError(null, 'notificationDays', t('enrollments.dateCalculationError')));
        return false;
      }

      onSave({
        billingInterval,
        dateStart: updatedDates.dateStart,
        dateEnd: updatedDates.dateEnd,
        dateDue: updatedDates.dateDue,
        dateSend: updatedDates.dateSend,
        notificationDays: details.notificationDays,
        serviceDirection: isServiceDatesEnabled ? serviceDirection : null,
        isServiceDatesEnabled,
        ...(isBillingIntervalChanged
          ? {
              intervalChangeEffectiveDue: updatedDates.dateDue,
              intervalChangePreviousBillingInterval: originalBillingInterval,
              location: planLocation?.id,
            }
          : {}),
        ...(isBillingIntervalChanged && isServiceDatesEnabled
          ? getIntervalTransitionPreviewFields({
              details,
              persistedEligibilityState,
              dateDue,
              dateStart,
              billingInterval,
              timezone,
            })
          : {}),
      });
      return true;
    }
  }

  function getIntervalTransitionPreviewFields({
    details,
    persistedEligibilityState,
    dateDue,
    dateStart,
    billingInterval,
    timezone,
  }) {
    const period = getIntervalTransitionServicePeriod({
      dateServiceEnd: persistedEligibilityState?.dateServiceEnd ?? details.dateServiceEnd,
      dateDue: persistedEligibilityState?.dateDue ?? dateDue,
      dateStart: details.dateStart ?? dateStart,
      billingInterval,
      timezone,
    });
    return {
      intervalChangeServicePeriodPreview: period.displayInterval,
      intervalChangePreviewServiceStart: period.dateServiceStart,
      intervalChangePreviewServiceEnd: period.dateServiceEnd,
    };
  }

  function onChangeBillingInterval(e, { value }) {
    setBillingInterval(value);
    setErrors(null);
  }
  function onChangeDateStart(e, { value }) {
    const date = moment(value, DefaultDateFormat).tz(timezone, true);
    const now = moment().tz(timezone).startOf('day');

    if (date.valueOf() < now.valueOf()) {
      value = now.format(DefaultDateFormat);
    }

    setDateStart(value);
    setDateEnd(null);
    setErrors(null);
  }
  function onChangeDateEnd(e, { value }) {
    setDateEnd(value);
    setIsDateEndChanged(true);
    setErrors(null);
  }
  function onChangeServiceDirection(value) {
    setServiceDirection(value);
    setErrors(null);
  }
  function onChangeServiceDatesEnabled() {
    setIsServiceDatesEnabled(!isServiceDatesEnabled);
    setErrors(null);
  }
}

function getIntervalChangeBlockMessage(reason, t) {
  const messages = {
    invalidServiceBoundary: t('billing.intervalChange.invalidServiceBoundary', {
      defaultValue:
        "We can't confirm this plan's last billing period. Reload Billing Plans. If this message still appears, contact Wonderschool Support.",
    }),
    pendingInvoice: t('billing.intervalChange.pendingInvoice', {
      defaultValue: 'A new invoice is already being made. Wait for it to finish, then try again.',
    }),
    planPaused: t('billing.intervalChange.planPaused', {
      defaultValue: 'This plan is paused. Go back to Billing Plans and select Resume Invoice Plan. Then try again.',
    }),
    planEnded: t('billing.intervalChange.planEnded', {
      defaultValue: 'This plan has ended, so it cannot be changed. Go back to Billing Plans and create a new plan.',
    }),
    planNotActive: t('billing.intervalChange.planNotActive', {
      defaultValue:
        "This plan isn't ready to be changed. Go back to Billing Plans and check the plan's status, then try again.",
    }),
    unsupportedServiceDirection: t('billing.intervalChange.unsupportedServiceDirection', {
      defaultValue: 'To change the billing schedule, select Next under the service period. Then try again.',
    }),
  };
  return messages[reason];
}
